import {
  SERVICES_NAMES,
  UPDATES_CANCEL_MODES,
  UPDATES_CLUSTER_STATUSES,
  UPDATES_OPERATIONS,
  UPDATES_TRANSITIONS
} from '@/common/constants/updates.constants';

import Vue from 'vue';
import i18n from '@/common/i18n';
import waitTask from '@/common/utils/waitTask';
import { EligibilityCheckV3 } from '@schema';
import useTasksStore from '@/common/store/pinia/tasks';
import { storeToRefs } from 'pinia';
import { versionSorter } from '@/common/utils/sorters/versionSorters';
import CancellationError from '@/common/utils/errors/CancellationError';
import deduplicateVersions from './dedublicateVersions';
import mapUpdateNode from './updateNode.mapper';
import mapUpdateService from './updateService.mapper';

const UPDATES_ELIGIBILITY_CHECK = 'software_updates_eligibility_check';

export default {
  name: 'updates',
  resource: '/api/v3/software_updates',
  Model: Object,
  plural: data => data,
  state: {
    currentOperation: '',
    transitionalStatus: '',
    eligibilityCheckStatus: null,
    error: null,
    releaseNotes: {},
    releaseNotesSeen: false,
    status: {
      statusName: '',
      lastCheck: null,
    },
    tasks: {
      checkUpdates: null,
      update: null,
      eligibilityCheck: null
    },
  },
  getters: {
    status: state => state.status,
    error: state => state.error,
    updateStatus: state => state.transitionalStatus || state.status.statusName,
    currentOperation: state => state.currentOperation,
    eligibilityCheckStatus: state => state.eligibilityCheckStatus,
    getActiveTask: (state) => {
      if (state.tasks?.download?.id) {
        return state.tasks.download;
      }
      if (state.tasks?.update?.id) {
        return state.tasks.update;
      }
      return null;
    },
    getUpdateTask: state => state.tasks.update,
    eligibilityCheckResult: (state) => {
      const list = state.eligibilityCheckStatus?.result || {};

      return Object.keys(list)
        .reduce((checks, name) => {
          const check = list[name];
          if (check.passed === false) {
            let details = [];
            if (check.exception) {
              details = details.concat(check.exception);
            }
            if (check.details) {
              details = details.concat(check.details);
            }

            checks.push({
              ...check,
              name,
              details
            });
          }
          return checks;
        }, []);
    },
    isMajorUpdate: (_, getters) => !!getters.getItems.find(item => item.isMajorUpdate),
    maxMajorVersion: (_, getters) => Math.max(...getters.getItems.map(item => item.availableMajorVersion || 0)),
    hasCriticals: (_, getters) => !!getters.eligibilityCheckResult?.find(check => check.severity === EligibilityCheckV3.severity.CRITICAL),
    hasWarnings: (_, getters) => getters.eligibilityCheckResult.length !== 0,
    releaseNotes: state => state.releaseNotes,
    releaseNotesSeen: state => state.releaseNotesSeen,
    eligibilityCheckPassed: (state, getters) => getters.eligibilityCheckResult?.every(res => res.passed),
    availableVersions: (state, getters) => deduplicateVersions(getters.getItems.flatMap(item => item.availableReleases).filter(Boolean))
      .sort(versionSorter)
      .reverse(),
    downloadedVersions: (state, getters) => deduplicateVersions(getters.getItems.map(item => item.downloadedRelease).filter(Boolean))
      .sort(versionSorter)
      .reverse(),
  },
  mutations: {
    SET_OPERATION(state, payload = '') {
      state.currentOperation = payload;
    },
    SET_TRANSITIONAL_STATUS(state, payload = '') {
      state.transitionalStatus = payload;
    },
    SET_STATUS(state, payload) {
      if (payload) {
        const {
          status,
          last_check_datetime: lastCheck = null,
        } = payload;

        Vue.set(state, 'status', {
          statusName: status || UPDATES_CLUSTER_STATUSES.NONE,
          lastCheck,
        });

        state.transitionalStatus = '';
      }
    },
    SET_RELEASE_NOTES(state, { id, data }) {
      Vue.set(state.releaseNotes, id, data);
    },
    SHOW_RELEASE_NOTES(state, payload) {
      state.releaseNotesSeen = payload;
    },
    SET_TELEMETRY(state, payload) {
      state.telemetry = payload;
    },
    SET_ELIGIBILITY_CHECK_STATUS(state, payload) {
      state.eligibilityCheckStatus = payload;
    },
    SET_ELIGIBILITY_CHECK_TASK(state, taskId) {
      if (!state.eligibilityCheckStatus) {
        state.eligibilityCheckStatus = { result: null };
      }
      state.eligibilityCheckStatus.task_id = taskId;
    },
    SET_UPDATE_TASK(state, task) {
      Vue.set(state.tasks, 'update', task);
    },
    SET_TASK(state, task) {
      state.tasks = { ...state.tasks, ...task };
    }
  },
  actions: ({ resource }) => ({
    GET_UPDATES_STATUS({ commit, dispatch, state }) {
      if (!state.currentOperation) {
        // do not disrupt a status of the current running operation
        commit('SET_OPERATION', UPDATES_OPERATIONS.CHECK_UPDATES);
      }

      return this.$api.get(resource)
        .then((res) => {
          const { nodes = [], control_plane: controlPlane, tasks, error } = res.data;

          const items = nodes
            .map(node => mapUpdateNode(node));

          if (controlPlane) {
            items.push(mapUpdateService({ ...controlPlane, name: SERVICES_NAMES.CONTROL_PLANE }));
          }

          dispatch('SET_ITEMS', { data: items });

          commit('SET_STATUS', res.data);

          error && commit('SET_ERRORS', error);

          if (tasks) {
            commit('SET_TASK', tasks);
          }

          return res;
        })
        .catch((error) => {
          commit('SET_ERRORS', error);
          commit('SET_STATUS', false);
          throw error;
        })
        .finally(() => {
          if (state.currentOperation === UPDATES_OPERATIONS.CHECK_UPDATES) {
            // do not disrupt a status of the current running operation
            commit('SET_OPERATION');
          }
        });
    },
    FETCH_RELEASE_NOTES({ commit }, { version, release }) {
      const id = `${version}-${release}`;
      return this.$api.get(`${resource}/release_notes/${id}`)
        .then(({ data }) => {
          commit('SET_RELEASE_NOTES', { id, data });
        });
    },
    CHECK_UPDATES({ commit }) {
      commit('SET_OPERATION', UPDATES_OPERATIONS.CHECK_UPDATES);

      return this.$api.post(`${resource}/check_for_update`)
        .then(waitTask)
        .finally(() => {
          commit('SET_OPERATION');
        });
    },
    DOWNLOAD_UPDATES({ commit }, { version, release }) {
      commit('SET_OPERATION', UPDATES_OPERATIONS.DOWNLOAD);

      return this.$api.post(`${resource}/download`, { version, release })
        .then(waitTask)
        .finally(() => {
          commit('SET_OPERATION');
        });
    },
    START_UPDATES({ commit }, payload) {
      commit('SET_OPERATION', UPDATES_OPERATIONS.START);

      return this.$api.post(`${resource}/start`, payload)
        .then(waitTask)
        .catch((error) => {
          if (error instanceof CancellationError) {
            return;
          }

          commit('SET_ERRORS', error);
          throw error;
        })
        .finally(() => {
          commit('SET_OPERATION');
        });
    },
    CANCEL_UPDATES({ commit, dispatch }, { maintenanceMode } = { maintenanceMode: UPDATES_CANCEL_MODES.LEAVE_IGNORE }) {
      commit('SET_OPERATION', UPDATES_OPERATIONS.CANCEL);

      dispatch('UPDATE_INSTALLING_UPDATES_NOTIFICATION', {
        main: false,
        bottom: false,
        tasks: true
      });

      return this.$api.delete(`${resource}/start`, { maintenance_mode: maintenanceMode })
        .then(waitTask)
        .finally(() => {
          commit('SET_OPERATION');
        });
    },
    PAUSE({ commit, dispatch }, { skip, force } = {}) {
      commit('SET_OPERATION', UPDATES_OPERATIONS.PAUSE);

      dispatch('UPDATE_INSTALLING_UPDATES_NOTIFICATION', {
        main: false,
        bottom: false,
        tasks: true
      });

      return this.$api.post(`${resource}/pause`, { skip, force })
        .then(waitTask)
        .finally(() => {
          commit('SET_OPERATION');
        });
    },
    RESUME({ commit, dispatch }, { skip, force } = {}) {
      commit('SET_OPERATION', UPDATES_OPERATIONS.RESUME);

      return this.$api.post(`${resource}/resume`, { skip, force })
        .then(waitTask)
        .finally(() => {
          dispatch('UPDATE_INSTALLING_UPDATES_NOTIFICATION', {
            main: true,
            bottom: true,
            tasks: true
          });
          commit('SET_OPERATION');
        });
    },
    ELIGIBILITY_CHECK_STATUS({ commit }) {
      commit('SET_OPERATION', UPDATES_OPERATIONS.CHECK_ELIGIBILITY);

      return this.$api.get(`${resource}/eligibility_check`)
        .then((response) => {
          commit('SET_ELIGIBILITY_CHECK_STATUS', response?.data);
          return response;
        })
        .finally(() => {
          commit('SET_OPERATION');
        });
    },
    ELIGIBILITY_CHECK({ commit, dispatch }) {
      commit('SET_OPERATION', UPDATES_OPERATIONS.CHECK_ELIGIBILITY);

      return this.$api.post(`${resource}/eligibility_check`, { event: UPDATES_ELIGIBILITY_CHECK })
        .then((response) => {
          commit('SET_ELIGIBILITY_CHECK_TASK', response?.data?.task_id);

          return response;
        })
        .then(waitTask)
        .then(() => dispatch('ELIGIBILITY_CHECK_STATUS'))
        .catch(error => {
          commit('SET_ELIGIBILITY_CHECK_STATUS', {
            result: { check: {
              severity: 'critical',
              passed: false,
              message: i18n.t('Failed to start update eligibility check'),
              details: [error.message]
            } }
          });
        })
        .finally(() => {
          commit('SET_ELIGIBILITY_CHECK_TASK');
          commit('SET_OPERATION');
        });
    },
    OPERATE_CLUSTER({ state, commit }, operation) {
      commit('SET_OPERATION', operation);
      const newStatus = UPDATES_TRANSITIONS
          .states[state.status?.statusName]?.on[operation]
        || UPDATES_TRANSITIONS.operations[operation];

      if (newStatus) {
        commit('SET_TRANSITIONAL_STATUS', newStatus);
      }

      return Promise.resolve();
    },
    UPDATE_INSTALLING_UPDATES_NOTIFICATION({ dispatch }, target) {
      const tasksStore = useTasksStore();

      const { installingUpdatesTask } = storeToRefs(tasksStore);

      if (installingUpdatesTask.value) {
        dispatch('notifications/UPDATE', {
          id: installingUpdatesTask.id,
          target
        }, { root: true });
      }
    },
    socket_softwareUpdates(_, data) {
      if (data.operation === 'status_changed') {
        this.$takeFirst('updates/GET_UPDATES_STATUS');
      }
    }
  }),
};
